import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import subscriptionService from "../../services/subscriptionService";
import { useAuth } from "../../contexts/AuthContext";
import { trackSignupClick } from "../../utils/analytics";
import { toast } from "react-hot-toast";
import { CheckIcon, SparklesIcon } from "@heroicons/react/24/solid";
import Button from "../ui/Button";

interface Plan {
  name: string;
  price?: string;
  monthlyPrice?: string;
  annualPrice?: string;
  monthlyPriceCAD?: string;
  annualPriceCAD?: string;
  subtitle?: string;
  description?: string;
  features: string[];
  popular?: boolean;
  buttonText: string;
  icon?: string;
  upgradeNote?: string;
  isPro?: boolean;
}

interface PricingCardProps {
  plan: Plan;
}

type Currency = "USD" | "CAD";

const PricingCard: React.FC<PricingCardProps> = ({ plan }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [currency, setCurrency] = useState<Currency>("USD");

  const handlePlanSelection = async () => {
    setLoading(true);

    try {
      // Si no hay usuario, siempre redirigir a signup para registro + selección de plan
      if (!user) {
        trackSignupClick(i18n.language, "pricing");
        navigate("/signup");
        return;
      }

      // Handle Enterprise/Community/Collective contact
      if (
        plan.name.toLowerCase().includes("enterprise") ||
        plan.name.toLowerCase().includes("community") ||
        plan.name.toLowerCase().includes("collective")
      ) {
        navigate("/contact");
        return;
      }

      // Handle Free/Explorer plan - si ya está logueado, llevarlo al dashboard
      if (
        plan.price?.includes("$0") ||
        plan.price === "Gratis" ||
        plan.price?.includes("0 $") ||
        plan.name.toLowerCase().includes("free") ||
        plan.name.toLowerCase().includes("explorer")
      ) {
        navigate("/dashboard");
        return;
      }

      // Determine plan ID (pro or expert)
      const planId =
        plan.isPro || plan.name.toLowerCase().includes("pro")
          ? "pro"
          : "expert";
      const billingInterval = isAnnual ? "annual" : "monthly";

      // Create Stripe checkout session and redirect
      const response = await subscriptionService.createStripeCheckoutSession(
        planId,
        billingInterval,
        currency.toLowerCase(),
      );

      if (response?.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error: any) {
      console.error("Error starting subscription:", error);
      toast.error(
        error.response?.data?.message ||
          t("pricingPage.subscriptionError", "Error starting subscription"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Check if this is a paid plan with monthly/annual pricing (Pro or Expert)
  const isPaidPlan = !!(plan.monthlyPrice && plan.annualPrice);

  // Check if this is Enterprise/Community/Collective (contact sales plan)
  const lowerName = plan.name.toLowerCase();
  const isCommunityPlan =
    lowerName.includes("enterprise") ||
    lowerName.includes("community") ||
    lowerName.includes("collective");

  // Get price based on currency and billing interval
  const getCurrentPrice = () => {
    if (currency === "CAD") {
      return isAnnual ? plan.annualPriceCAD : plan.monthlyPriceCAD;
    }
    return isAnnual ? plan.annualPrice : plan.monthlyPrice;
  };

  // Expert Plan Card (Best Seller with internal toggle)
  if (isPaidPlan && plan.popular) {
    const currentPrice = getCurrentPrice();
    const priceSuffix = isAnnual
      ? t("pricingPage.perYear", "/year")
      : t("pricingPage.perMonth", "/month");

    return (
      <div className="relative">
        {/* Best Seller Badge */}
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
          <span className="bg-gradient-to-r from-[#00e9be] to-emerald-400 text-[#00342d] text-xs font-bold px-4 py-2 rounded-full shadow-lg uppercase tracking-wide flex items-center gap-1">
            <SparklesIcon className="w-4 h-4" />
            {t("pricingPage.bestSeller", "BEST SELLER")}
          </span>
        </div>

        <div className="relative flex flex-col p-8 rounded-2xl bg-gradient-to-br from-[#00342d] to-[#004d40] border-2 border-[#00e9be]/50 shadow-2xl shadow-[#00e9be]/20">
          <div className="mb-6 pt-4">
            <h3 className="text-2xl font-bold text-white mb-4">{plan.name}</h3>

            {/* Currency Toggle */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <button
                onClick={() => setCurrency("USD")}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-300 ${
                  currency === "USD"
                    ? "bg-[#00e9be] text-[#00342d]"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                USD
              </button>
              <button
                onClick={() => setCurrency("CAD")}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-300 ${
                  currency === "CAD"
                    ? "bg-[#00e9be] text-[#00342d]"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                CAD
              </button>
            </div>

            {/* Internal Toggle Switch */}
            <div className="flex items-center justify-center gap-3 mb-4 bg-white/10 rounded-full p-2">
              <span
                className={`text-sm font-medium transition-colors duration-300 ${
                  !isAnnual ? "text-[#00e9be]" : "text-white/60"
                }`}
              >
                {t("pricingPage.monthly", "Monthly")}
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none ${
                  isAnnual ? "bg-[#00e9be]" : "bg-white/30"
                }`}
                aria-label={isAnnual ? "Switch to monthly" : "Switch to annual"}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                    isAnnual ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
              <div className="flex flex-col items-start">
                <span
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isAnnual ? "text-[#00e9be]" : "text-white/60"
                  }`}
                >
                  {t("pricingPage.annual", "Annual")}
                </span>
              </div>
            </div>

            {/* Price Display */}
            <div className="flex items-end gap-2 justify-center">
              <span className="text-4xl font-bold text-[#00e9be]">
                {currentPrice}
              </span>
              <span className="text-white/70 text-sm mb-1">{priceSuffix}</span>
            </div>

            {isAnnual && (
              <div className="mt-3 text-center">
                <span className="inline-flex items-center gap-1 bg-[#00e9be]/20 text-[#00e9be] text-xs font-semibold px-3 py-1 rounded-full">
                  <SparklesIcon className="w-3 h-3" />
                  {t("pricingPage.twoMonthsFree", "2 months free")} •{" "}
                  {t("pricingPage.savingsNote", "Save 17%")}
                </span>
              </div>
            )}
          </div>

          <div className="mb-6">
            <ul className="space-y-3">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#00e9be]/20 flex items-center justify-center mt-0.5">
                    <CheckIcon className="w-3 h-3 text-[#00e9be]" />
                  </div>
                  <span className="text-white/90 text-sm leading-relaxed">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <Button
            onClick={handlePlanSelection}
            disabled={loading}
            variant="primary"
            size="md"
            className="w-full font-bold text-base py-4 bg-[#00e9be] text-[#00342d] hover:bg-[#00d1aa] shadow-lg"
          >
            {loading ? "Processing..." : plan.buttonText}
          </Button>
        </div>
      </div>
    );
  }

  // Pro Plan Card (paid plan but not "popular"/best seller)
  if (isPaidPlan && !plan.popular) {
    const currentPrice = getCurrentPrice();
    const priceSuffix = isAnnual
      ? t("pricingPage.perYear", "/year")
      : t("pricingPage.perMonth", "/month");

    return (
      <div className="relative flex flex-col p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg hover:shadow-xl hover:border-blue-300 transition-all duration-300">
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-[#00342d] mb-3">
            {plan.name}
          </h3>

          {/* Currency Toggle */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <button
              onClick={() => setCurrency("USD")}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-300 ${
                currency === "USD"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              USD
            </button>
            <button
              onClick={() => setCurrency("CAD")}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-300 ${
                currency === "CAD"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              CAD
            </button>
          </div>

          {/* Internal Toggle Switch */}
          <div className="flex items-center justify-center gap-3 mb-4 bg-white/70 rounded-full p-2">
            <span
              className={`text-sm font-medium transition-colors duration-300 ${
                !isAnnual ? "text-blue-600" : "text-gray-400"
              }`}
            >
              {t("pricingPage.monthly", "Monthly")}
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none ${
                isAnnual ? "bg-blue-600" : "bg-gray-300"
              }`}
              aria-label={isAnnual ? "Switch to monthly" : "Switch to annual"}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                  isAnnual ? "translate-x-6" : "translate-x-0"
                }`}
              />
            </button>
            <span
              className={`text-sm font-medium transition-colors duration-300 ${
                isAnnual ? "text-blue-600" : "text-gray-400"
              }`}
            >
              {t("pricingPage.annual", "Annual")}
            </span>
          </div>

          {/* Price Display */}
          <div className="flex items-end gap-2 justify-center">
            <span className="text-4xl font-bold text-blue-600">
              {currentPrice}
            </span>
            <span className="text-gray-500 text-sm mb-1">{priceSuffix}</span>
          </div>

          {isAnnual && (
            <div className="mt-3 text-center">
              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                <SparklesIcon className="w-3 h-3" />
                {t("pricingPage.twoMonthsFree", "2 months free")} •{" "}
                {t("pricingPage.savingsNote", "Save 17%")}
              </span>
            </div>
          )}

          {plan.description && (
            <p className="text-gray-600 text-sm mt-3 text-center">
              {plan.description}
            </p>
          )}
        </div>

        <div className="mb-6">
          <ul className="space-y-3">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <CheckIcon className="w-3 h-3 text-blue-600" />
                </div>
                <span className="text-gray-700 text-sm leading-relaxed">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <Button
          onClick={handlePlanSelection}
          disabled={loading}
          variant="primary"
          size="md"
          className="w-full font-bold text-base py-3 bg-blue-600 text-white hover:bg-blue-700"
        >
          {loading ? "Processing..." : plan.buttonText}
        </Button>
      </div>
    );
  }

  // Explorer or Community Card
  return (
    <div className="relative flex flex-col p-6 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg hover:border-[#00e9be]/30 transition-all duration-300">
      <div className="mb-4">
        <h3 className="text-2xl font-bold text-[#00342d] mb-1">{plan.name}</h3>
        {/* Italic subtitle for Collective plan */}
        {plan.subtitle && (
          <p className="text-gray-500 text-sm italic mb-3">{plan.subtitle}</p>
        )}
        {/* Description like "Includes everything in the Expert Plan, plus:" */}
        {isCommunityPlan && plan.description && (
          <p className="text-gray-700 text-sm font-medium mb-2">
            {plan.description}
          </p>
        )}
        {!isCommunityPlan && (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-[#00342d]">
              {plan.price?.split("/")[0] || plan.price}
            </span>
            {plan.price &&
              !plan.price.includes("$0") &&
              !plan.price.includes("0 $") &&
              plan.price !== "Gratis" && (
                <span className="text-gray-500 text-sm">
                  {t("pricingPage.perMonth", "/month")}
                </span>
              )}
          </div>
        )}
      </div>

      <div className="mb-6">
        <ul className="space-y-3">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e6fffa] flex items-center justify-center mt-0.5">
                <CheckIcon className="w-3 h-3 text-[#00e9be]" />
              </div>
              <span className="text-gray-600 text-sm leading-relaxed">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Upgrade Note for Explorer */}
      {plan.upgradeNote && (
        <div className="mb-4 p-3 bg-[#f0fdf9] rounded-xl border border-[#00e9be]/20">
          <p className="text-xs text-[#00342d]/80 font-medium flex items-center gap-2">
            <SparklesIcon className="w-4 h-4 text-[#00e9be] flex-shrink-0" />
            {plan.upgradeNote}
          </p>
        </div>
      )}

      <Button
        onClick={handlePlanSelection}
        disabled={loading}
        variant={isCommunityPlan ? "primary" : "outline"}
        size="md"
        className={`w-full font-bold text-base py-3 ${
          isCommunityPlan
            ? "bg-[#00342d] text-white hover:bg-[#004d40]"
            : "border-[#00342d] text-[#00342d] hover:bg-[#00342d] hover:text-white"
        }`}
      >
        {loading ? "Processing..." : plan.buttonText}
      </Button>
    </div>
  );
};

export default PricingCard;
